import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { healthQueryOptions } from "../api/queries";
import { useSession } from "../api/session";
import { auditLogQueryOptions } from "../api/settings";
import { AuditFeedItem } from "../components/audit";
import { ActionPillLink, SectionRowLink } from "../components/links";
import { PageHeader } from "../components/shell/app-shell";
import { Card, cardActionClassName, CardHeader } from "../components/ui/card";
import { Checklist } from "../components/ui/checklist";
import { Empty } from "../components/ui/empty";
import { SectionRow, type SectionRowStatus } from "../components/ui/section-row";
import { Skeleton } from "../components/ui/skeleton";
import { SETUP_STEPS } from "../demo-data/checklist";
import { DEMO_TODAY } from "../demo-data/organization";
import { SECTION_OPEN_ITEMS } from "../demo-data/sections";
import { COMING_SOON_SECTIONS, SECTION_IDS, SECTION_LABELS, SECTION_PATHS } from "../domain/sections";
import { formatLongDate } from "../lib/format";

export const Route = createFileRoute("/")({
	head: () => ({ meta: [{ title: "Home · Ravnar" }] }),
	component: HomePage,
});

/** "Ada" for "Ada Lovelace". */
function firstName(name: string): string {
	return name.split(" ", 1)[0] ?? name;
}

function sectionStatus(openItems: number): SectionRowStatus {
	if (openItems === 0) {
		return "idle";
	}
	return openItems > 10 ? "attention" : "ok";
}

function HomePage() {
	const [showChecklist, setShowChecklist] = useState(true);
	// Greets whoever is signed in by the first name Settings › Team & Access lists for them.
	const { member } = useSession();
	// A section still to come has nothing that needs anyone yet.
	const openTotal = SECTION_IDS.filter(id => !COMING_SOON_SECTIONS.has(id)).reduce(
		(total, id) => total + SECTION_OPEN_ITEMS[id],
		0,
	);

	return (
		<>
			<PageHeader
				title={`Hi, ${firstName(member.name)}.`}
				description={`${formatLongDate(DEMO_TODAY)} · ${openTotal} items need your attention`}
			/>
			{showChecklist ? (
				<Checklist
					title="Finish setting up"
					items={SETUP_STEPS}
					onDismiss={() => {
						setShowChecklist(false);
					}}
				/>
			) : null}
			<div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1fr_320px]">
				<div className="flex flex-col gap-3.5">
					<Card>
						<CardHeader
							title="Overview"
							action={
								<Link to="/work" className={cardActionClassName}>
									Open work →
								</Link>
							}
						/>
						{SECTION_IDS.map(id => {
							if (COMING_SOON_SECTIONS.has(id)) {
								return (
									<SectionRow
										key={id}
										name={SECTION_LABELS[id]}
										status="idle"
										statusText="Coming soon"
									/>
								);
							}
							const openItems = SECTION_OPEN_ITEMS[id];
							return (
								<SectionRowLink
									key={id}
									to={SECTION_PATHS[id]}
									name={SECTION_LABELS[id]}
									status={sectionStatus(openItems)}
									statusText={
										openItems === 0 ? "Nothing pending" : `${openItems} items need attention`
									}
								/>
							);
						})}
					</Card>
					<div className="flex flex-col gap-2">
						<ActionPillLink to="/finance" tone="alert" count={8}>
							Overdue invoices need chasing
						</ActionPillLink>
						{COMING_SOON_SECTIONS.has("legal") ? null : (
							<ActionPillLink to="/legal" tone="warn" count={3}>
								Contracts renew within 30 days
							</ActionPillLink>
						)}
						<ActionPillLink to="/finance" tone="info" count={12}>
							Receipts waiting to be filed
						</ActionPillLink>
					</div>
				</div>
				<div className="flex flex-col gap-3.5 self-start">
					{/* Only owners and admins may read the audit log; a member's Home shows nothing of it and asks nothing. */}
					{member.role === "member" ? null : <RecentActivityCard />}
					<SystemStatusCard />
				</div>
			</div>
		</>
	);
}

/**
 * The newest page of the organization's audit log, as feed rows, with the way to the whole log. Its query asks again on
 * its own, as the Audit log page's newest page does, so what happens while Home is open shows up here.
 */
function RecentActivityCard() {
	const { data, isPending, isError, error } = useQuery(auditLogQueryOptions());
	return (
		<Card>
			<CardHeader
				title="Recent activity"
				action={
					<Link to="/settings/audit-log" className={cardActionClassName}>
						Open audit log →
					</Link>
				}
			/>
			{isPending ? (
				<div role="status" aria-label="Loading recent activity" className="flex flex-col gap-3 p-4">
					<Skeleton className="h-3.5 w-2/3" />
					<Skeleton className="h-3.5 w-1/2" />
				</div>
			) : isError ? (
				<p role="alert" className="px-4 py-3.5 text-caption text-alert">
					{error.message}
				</p>
			) : data.entries.length === 0 ? (
				<Empty>Nothing recorded yet.</Empty>
			) : (
				<ul aria-label="Recent activity" className="thin-scroll max-h-[460px] overflow-y-auto">
					{data.entries.map(entry => (
						<AuditFeedItem key={entry.id} entry={entry} />
					))}
				</ul>
			)}
		</Card>
	);
}

function SystemStatusCard() {
	// Non-suspending query: the page stays usable even when the API is down.
	const health = useQuery(healthQueryOptions());
	return (
		<Card>
			<CardHeader title="System" />
			<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 px-4 py-3 text-caption">
				<dt className="text-ink-3">API status</dt>
				<dd data-testid="api-status" className="text-ink">
					{health.isPending
						? "checking…"
						: health.isError
							? `unavailable (${health.error.message})`
							: health.data.status}
				</dd>
				<dt className="text-ink-3">Stage</dt>
				<dd className="text-ink">{health.data?.stage ?? "–"}</dd>
			</dl>
		</Card>
	);
}
